interface DataUrlCacheEntry {
  value: string
  size: number
}

const MAX_CACHE_BYTES = 64 * 1024 * 1024

const cache = new Map<string, DataUrlCacheEntry>()
const inFlight = new Map<string, Promise<string>>()
let cacheBytes = 0

function getSize(value: string): number {
  return value.length * 2
}

function touch(key: string, entry: DataUrlCacheEntry): void {
  cache.delete(key)
  cache.set(key, entry)
}

function trimCache(): void {
  while (cacheBytes > MAX_CACHE_BYTES) {
    const oldest = cache.entries().next().value as [string, DataUrlCacheEntry] | undefined
    if (!oldest) break
    cache.delete(oldest[0])
    cacheBytes -= oldest[1].size
  }
}

export function getCachedDataUrl(key: string): string | undefined {
  const entry = cache.get(key)
  if (!entry) return undefined
  touch(key, entry)
  return entry.value
}

export async function loadCachedDataUrl(
  key: string,
  load: () => Promise<string>,
): Promise<string> {
  const cached = getCachedDataUrl(key)
  if (cached) return cached

  const existing = inFlight.get(key)
  if (existing) return existing

  const promise = load()
    .then((value) => {
      const previous = cache.get(key)
      if (previous) {
        cacheBytes -= previous.size
      }

      const entry = { value, size: getSize(value) }
      cache.set(key, entry)
      cacheBytes += entry.size
      trimCache()
      return value
    })
    .finally(() => {
      inFlight.delete(key)
    })

  inFlight.set(key, promise)
  return promise
}

export function clearDataUrlCache(): void {
  cache.clear()
  inFlight.clear()
  cacheBytes = 0
}

export function getDataUrlCacheStats(): { entries: number; bytes: number; inFlight: number } {
  return {
    entries: cache.size,
    bytes: cacheBytes,
    inFlight: inFlight.size,
  }
}
